import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, TrendingUp, Calculator, Building, Briefcase, Globe, Star, CheckCircle, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import './LandingExpert.css';
import './components/FinancialStrategy.css';

interface LandingExpertProps {
  onNavigateBack: () => void;
  onStartSimulator: () => void;
  onStartInscription: () => void;
  onOpenMonCompte: () => void;
  onStartChat: () => void;
}

const LandingExpert: React.FC<LandingExpertProps> = ({ 
  onNavigateBack, 
  onStartSimulator, 
  onStartInscription, 
  onOpenMonCompte,
  onStartChat
}) => {
  // État pour le carousel d'images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // État pour le menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const carouselImages = [
    {
      src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Dashboard financier avancé",
      title: "Tableaux de bord experts",
      description: "Visualisation en temps réel"
    },
    {
      src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Analyse de données financières",
      title: "Analyse quantitative",
      description: "Modèles sophistiqués"
    },
    {
      src: "https://images.unsplash.com/photo-1590479773265-7464e5d48118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Stratégie d'investissement",
      title: "Stratégies personnalisées",
      description: "Allocation optimisée"
    },
    {
      src: "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      alt: "Performance portfolio",
      title: "Performance supérieure",
      description: "Rendements optimisés"
    }
  ];

  // Auto-scroll du carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };
  return (
    <div className="hero-background">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <div className="service-icon-teal">
                <Star className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold hero-title-gradient">
                SOeasi
              </h1>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="#solutions" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Stratégies</a>
              <a href="#avantages" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Avantages</a>
              <a href="#tarifs" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Performance</a>
              <a href="#contact" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Expertise</a>
            </nav>
            <div className="flex items-center justify-end space-x-2 flex-shrink-0">
              {/* Boutons desktop */}
              <div className="hidden md:flex items-center space-x-4">
               
                <button 
                  onClick={onOpenMonCompte}
                  className="px-4 py-2 text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap"
                  style={{
                    backgroundColor: '#1e4586',
                    color: 'white',
                    boxShadow: '0 0 10px rgba(30, 69, 134, 0.3)',
                    border: '1px solid rgba(30, 69, 134, 0.5)'
                  }}
                >
                  Espace client
                </button>
                <button 
                  onClick={onStartChat}
                  className="btn-primary whitespace-nowrap"
                >
                  Nous contacter
                </button>
              </div>
              
              {/* Menu mobile avec boutons essentiels */}
              <div className="md:hidden flex items-center space-x-2">
             
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-colors border border-gray-300"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Menu mobile */}
        <div className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-4 py-4 space-y-3">
            {/* Navigation links */}
            <div className="divide-y divide-gray-200">
              <a 
                href="#solutions" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Stratégies
              </a>
              <a 
                href="#avantages" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Avantages
              </a>
              <a 
                href="#tarifs" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Performance
              </a>
              <a 
                href="#contact" 
                className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50  transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Expertise
              </a>
            </div>
            
            {/* Boutons d'action mobile */}
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
              <button 
                onClick={() => {
                  onOpenMonCompte();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg text-white"
                style={{
                  backgroundColor: '#1e4586',
                  boxShadow: '0 0 10px rgba(30, 69, 134, 0.3)'
                }}
              >
                Espace client
              </button>
              <button 
                onClick={() => {
                  onStartChat();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full btn-primary"
              >
                Nous contacter
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Version   */}
      <section className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-block bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Solutions pour investisseurs expérimentés
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Maximisez votre{' '}
                  <span className="hero-title-gradient">
                    performance
                  </span>
                  {' '}d'investissement
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  SOeasi Expert s'adresse aux investisseurs avertis qui recherchent des stratégies 
                  sophistiquées et des rendements optimisés. Accédez à des opportunités exclusives 
                  et bénéficiez d'outils d'analyse avancés.
                </p>
              </div>

              {/* CTA Principal */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onStartSimulator}
                  className="group bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <TrendingUp className="w-6 h-6" />
                  <span>Analyser mon portefeuille</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={onStartChat}
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 flex items-center space-x-3"
                >
                  <Star className="w-6 h-6" />
                  <span>Consultation expert</span>
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">50€+</div>
                  <p className="text-sm text-gray-600">Ticket minimum</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">8%</div>
                  <p className="text-sm text-gray-600">Performance annuelle</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">200+</div>
                  <p className="text-sm text-gray-600">Investisseurs experts</p>
                </div>
              </div>
            </div>

            {/* Image   */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80"
                  alt="Équipe de conseillers financiers en réunion"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                
                {/* Éléments flottants */}
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-800">Gestion déléguée</span>
                  </div>
                </div>
                
                <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-800">Performance optimisée</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stratégie Financière Sur Mesure Section */}
      <section id="solutions" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-100 to-emerald-100 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-6 border border-blue-200">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Stratégie patrimoniale experte</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="text-gray-900">Stratégie financière</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-emerald-500">
                sur mesure
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Chaque investisseur expert mérite une approche unique. Nous développons des stratégies 
              personnalisées qui s'adaptent à votre profil de risque, vos objectifs et votre horizon d'investissement.
            </p>
          </div>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Contenu principal */}
            <div className="space-y-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-blue-100/50 hover:shadow-2xl transition-all duration-300 group">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Analyse patrimoniale approfondie</h3>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full"></div>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Notre équipe d'experts analyse votre situation patrimoniale complète pour identifier 
                  les opportunités d'optimisation et de croissance en intégrant nos 3 produits privilégiés.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Audit patrimonial complet</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Analyse fiscale avancée</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Évaluation des risques</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Projection de performance</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100/50 hover:shadow-2xl transition-all duration-300 group">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Allocation optimisée SCPI • GFI • Sukuk</h3>
                    <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-emerald-300 rounded-full"></div>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Stratégie d'allocation personnalisée combinant nos 3 produits phares selon votre 
                  profil de risque et vos objectifs patrimoniaux pour une diversification optimale.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Immobilier + Infrastructure + Finance islamique</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Rendements décorrélés des marchés</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Revenus réguliers et prévisibles</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Protection contre l'inflation</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Carousel d'images moderne */}
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 overflow-hidden">
                <div className="text-center p-6 pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Excellence SOeasi Expert</h4>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full mx-auto"></div>
                </div>
                
                {/* Container du carousel */}
                <div className="relative h-80 overflow-hidden">
                  {carouselImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                        index === currentImageIndex ? 'translate-x-0' : 
                        index < currentImageIndex ? '-translate-x-full' : 'translate-x-full'
                      }`}
                    >
                      <div className="relative h-full">
                        <img 
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        
                        {/* Overlay de texte */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h5 className="text-lg font-bold mb-1">{image.title}</h5>
                          <p className="text-sm text-white/90">{image.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Boutons de navigation */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 group"
                  >
                    <ChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 group"
                  >
                    <ChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
                
                {/* Indicateurs */}
                <div className="flex justify-center gap-2 p-6 pt-4">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-gradient-to-r from-blue-500 to-emerald-500 scale-125' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="px-6 pb-6">
                  <div className="p-4 bg-gradient-to-r from-blue-50/50 to-emerald-50/50 rounded-2xl border border-blue-200/30">
                    <p className="text-xs text-gray-700 text-center leading-relaxed">
                      <span className="font-semibold">Découvrez</span> nos outils experts et tableaux de bord avancés 
                      conçus pour optimiser vos investissements SCPI, GFI et Sukuk.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action moderne */}
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-emerald-500 rounded-3xl p-10 text-white relative overflow-hidden">
              {/* Décoration de fond */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold mb-6">Prêt pour une stratégie personnalisée ?</h3>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                  Nos experts analysent votre situation et conçoivent une stratégie d'investissement 
                  parfaitement adaptée à vos objectifs patrimoniaux avec nos 3 produits privilégiés.
                </p>
                <button
                  onClick={onStartSimulator}
                  className="bg-white/95 text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white transition-all duration-300 inline-flex items-center space-x-3 shadow-2xl hover:shadow-xl hover:scale-105 transform"
                >
                  <TrendingUp className="w-6 h-6" />
                  <span>Commencer mon analyse</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section id="avantages" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                Pourquoi choisir SOeasi   ?
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Star className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Expertise reconnue</h3>
                    <p className="text-gray-600">
                      Plus de 10 ans d'expérience dans la gestion patrimoniale d'entreprise. 
                      Équipe d'experts certifiés AMF.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Solutions sur mesure</h3>
                    <p className="text-gray-600">
                      Chaque entreprise est unique. Nous concevons des solutions parfaitement 
                      adaptées à votre secteur et vos objectifs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sécurité maximale</h3>
                    <p className="text-gray-600">
                      Tous nos investissements respectent les réglementations en vigueur. 
                      Transparence totale sur les risques et performances.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80"
                alt="Dashboard  "
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Stratégie Financière Complète - Version Expert */}
      <section className="py-12 md:py-20 relative overflow-hidden" style={{backgroundColor: '#082F49'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-flex items-center bg-blue-500/10 text-blue-400 px-6 py-3 rounded-full text-sm font-medium mb-6 border border-blue-500/20">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Excellence pour investisseurs avertis</span>
            </div>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              Stratégies d'investissement{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
                complètes
              </span>
            </h2>
            <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              SOeasi Expert déploie une approche institutionnelle qui combine analyse quantitative, 
              diversification alternative, gestion des risques avancée et optimisation fiscale 
              pour maximiser la performance ajustée du risque de votre portefeuille.
            </p>
          </div>

          {/* Network Visualization - Version Expert */}
          <div className="relative flex items-center justify-center min-h-[400px] sm:min-h-[500px] md:min-h-[700px] lg:min-h-[800px] max-w-6xl mx-auto mt-6 md:mt-12">
            
            {/* Central Hub - Portfolio Management */}
            <div className="relative z-20 animate-node-center">
              <div className="w-24 h-24 md:w-32 lg:w-40 h-24 md:h-32 lg:h-40 bg-slate-700 rounded-full border-2 md:border-4 border-blue-400 flex items-center justify-center shadow-2xl relative">
                {/* Cercles concentriques décoratifs */}
                <div className="absolute inset-0 rounded-full border-2 border-blue-400/20 animate-pulse"></div>
                <div className="absolute inset-2 rounded-full border border-blue-400/10"></div>
                
                <div className="text-center relative z-10">
                  <div className="w-8 h-8 md:w-10 lg:w-12 h-8 md:h-10 lg:h-12 mx-auto mb-1 md:mb-2 bg-blue-400 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 md:w-5 lg:w-6 h-4 md:h-5 lg:h-6 text-slate-800" />
                  </div>
                  <h3 className="text-white font-bold text-xs md:text-sm lg:text-base">Portfolio</h3>
                  <h3 className="text-blue-400 font-bold text-xs md:text-xs">Expert</h3>
                </div>
              </div>
            </div>

            {/* Orbit Container avec rotation */}
            <div className="absolute inset-0 orbit-container">
              
              {/* Investissements Alternatifs - Position 0° (haut) */}
              <div className="node-position-1 animate-node-1">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Building className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Alternatifs</h4>
                    <p className="text-gray-300 text-xs mt-1">Private Equity, Hedge Funds, REITs</p>
                  </div>
                </div>
              </div>

              {/* Analyse Quantitative - Position 60° (haut droite) */}
              <div className="node-position-2 animate-node-2">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Analyse</h4>
                    <h4 className="text-white font-bold text-sm">Quantitative</h4>
                    <p className="text-gray-300 text-xs mt-1">Modèles algorithmiques avancés</p>
                  </div>
                </div>
              </div>

              {/* Gestion des Risques - Position 120° (bas droite) */}
              <div className="node-position-3 animate-node-3">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Gestion des</h4>
                    <h4 className="text-white font-bold text-sm">Risques</h4>
                    <p className="text-gray-300 text-xs mt-1">VaR, tests de stress, couverture</p>
                  </div>
                </div>
              </div>

              {/* Structuration Fiscale - Position 180° (bas) */}
              <div className="node-position-4 animate-node-4">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Structuration</h4>
                    <h4 className="text-white font-bold text-sm">Fiscale</h4>
                    <p className="text-gray-300 text-xs mt-1">Optimisation juridique et fiscale</p>
                  </div>
                </div>
              </div>

              {/* Trading Algorithmique - Position 240° (bas gauche) */}
              <div className="node-position-5 animate-node-5">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Trading</h4>
                    <h4 className="text-white font-bold text-sm">Algorithmique</h4>
                    <p className="text-gray-300 text-xs mt-1">Exécution automatisée optimisée</p>
                  </div>
                </div>
              </div>

              {/* ESG & Impact - Position 300° (haut gauche) */}
              <div className="node-position-6 animate-node-6">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-blue-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                      <Globe className="w-6 h-6 text-slate-800" />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">ESG &</h4>
                    <h4 className="text-white font-bold text-sm">Impact</h4>
                    <p className="text-gray-300 text-xs mt-1">Investissement responsable</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Lignes de connexion animées */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
              <defs>
                <radialGradient id="connectionGradientExpert" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
                </radialGradient>
              </defs>
              
              {/* Lignes pointillées du centre vers chaque nœud */}
              <g stroke="url(#connectionGradientExpert)" strokeWidth="2" strokeDasharray="4,4" fill="none">
                <line x1="50%" y1="50%" x2="50%" y2="15%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="0.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="50%" y2="85%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2.5s" repeatCount="indefinite"/>
                </line>
              </g>
            </svg>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-8 md:mt-16">
            <button
              onClick={onStartSimulator}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 mx-auto"
            >
              <span>Analyser mon portefeuille</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section -   - HIDDEN TEMPORARILY */}
      {false && (
        <section id="tarifs" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Nos offres  
              </h2>
              <p className="text-xl text-gray-600">
                Des tarifs transparents adaptés à la taille de votre entreprise
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Plan Starter */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Starter</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur devis</div>
                <p className="text-gray-600 mb-6">Pour les PME (&lt; 50 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Gestion de base</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Support par email</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting mensuel</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  Demander un devis
                </button>
              </div>

              {/* Plan Professional */}
              <div className="bg-gradient-to-t from-blue-50 to-white rounded-2xl p-8 shadow-xl border-2 border-blue-200 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Le plus populaire
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Professional</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur devis</div>
                <p className="text-gray-600 mb-6">Pour les entreprises (50-250 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Gestion avancée</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Conseiller dédié</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting hebdomadaire</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Formation équipes</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  Demander un devis
                </button>
              </div>

              {/* Plan Enterprise */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Enterprise</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">Sur mesure</div>
                <p className="text-gray-600 mb-6">Pour les grandes entreprises (&gt; 250 salariés)</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Solution sur mesure</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Équipe dédiée</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>Reporting temps réel</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>API intégration</span>
                  </li>
                </ul>
                
                <button
                  onClick={onStartChat}
                  className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  Nous contacter
                </button>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Footer   */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">SOeasi  </h3>
              </div>
              <p className="text-gray-400">
                Solutions patrimoniales professionnelles pour entreprises et dirigeants.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Gestion d'entreprise</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Épargne salariale</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Conseils dirigeants</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Optimisation fiscale</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Formations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support technique</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li> @soeasi.fr</li>
                <li>+33 1 XX XX XX XX</li>
                <li>Disponible 24h/24 - 7j/7</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SOeasi  . Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingExpert;